/**
 * Typed Facade over the `posthog-js` singleton; the single import boundary
 * for product analytics. Convention reference and decision rationale live in
 * `docs/solutions/best-practices/posthog-event-tracking.md`.
 *
 * PII rule (load-bearing): property values are entity IDs and categorical
 * fields only, never free text, prompt content, document/conversation
 * titles, recipient emails, or any other user-generated string. Identity
 * (`user_id`) and tenant (`organization` group) are auto-attached via
 * `posthog.identify()` / `posthog.group()` in the app shell, so do NOT pass
 * `organization_id` (or any user identity field) in property bags.
 */
import { BaseExportType } from "@/api/scopes/scopes.types";
import posthog from "posthog-js";

/**
 * Fires when an invited user completes account creation. Runs pre-identify
 * (the acceptance response carries no user/org id) so it lands on the
 * anonymous distinct_id and is stitched once `identify` runs on the next
 * protected route. `is_shared_conversation_invite` distinguishes
 * invite-to-collaborate deep-links (invitee sent straight to a shared
 * conversation) from plain org invites.
 */
export function trackUserSignedUp(props: {
    invitation_id: number;
    is_shared_conversation_invite: boolean;
}): void {
    posthog.capture("User Signed Up", props);
}

export function trackChatSessionStarted(props: {
    conversation_id: number;
    agent_id?: number;
}): void {
    posthog.capture("User Started Chat Session", props);
}

export function trackWorkflowStarted(props: {
    conversation_id: number;
    agent_id: number;
    agent_name: string;
}): void {
    posthog.capture("User Started Workflow", props);
}

/** `is_refinement: true` distinguishes refine-button retries from typed messages. */
export function trackChatMessageSent(props: {
    conversation_id: number;
    attachment_count: number;
    is_refinement: boolean;
}): void {
    posthog.capture("User Sent Chat Message", props);
}

export function trackAgentShortcutClicked(props: { agent_id: number }): void {
    posthog.capture("User Clicked Agent Shortcut", props);
}

export function trackChatSessionShared(props: {
    conversation_id: number | string;
    share_mode: "org_member" | "invited";
}): void {
    posthog.capture("User Shared Chat Session", props);
}

/**
 * Fires once per real org-wide enable of a Polly conversation — on the toggle's
 * `newly_enabled` off→on transition only; re-enabling an already-team-visible
 * thread, and disabling, are silent. `member_count` is the number of org
 * teammates notified; IDs + a count only, never org_id / title / emails (PII
 * rule above).
 */
export function trackChatSessionSharedWithTeam(props: {
    conversation_id: number;
    member_count: number;
}): void {
    posthog.capture("User Shared Chat Session With Team", props);
}

export function trackDocumentUploaded(props: {
    library_document_id: number;
    folder_id: number | null;
    is_knowledge_base: boolean;
    source: "chat_upload" | "library_upload" | "scope_create_upload";
}): void {
    posthog.capture("User Uploaded Document", props);
}

export function trackScopeCreated(props: {
    scope_id: number;
    clause_checklist_id: number;
    industry_id?: number;
    field_extraction_enabled: boolean;
    source: "library" | "upload";
}): void {
    posthog.capture("User Created Scope", props);
}

export function trackComparisonCreated(props: {
    comparison_id: number;
    clause_checklist_id: number;
    scope_count: number;
}): void {
    posthog.capture("User Created Comparison", props);
}

export function trackScopeViewed(props: {
    scope_id: number;
    is_v2: boolean;
    status: string;
}): void {
    posthog.capture("User Viewed Scope", props);
}

/** `textract_status` is null right after upload, before processing starts. */
export function trackDocumentViewed(props: {
    document_id: number;
    folder_id: number | null;
    is_knowledge_base: boolean;
    textract_status: string | null;
}): void {
    posthog.capture("User Viewed Document", props);
}

export function trackArtifactExported(props: {
    chat_id: string | null;
    artifact_type: "xlsx";
    row_count: number;
    column_count: number;
}): void {
    posthog.capture("User Exported Artifact", props);
}

/** `column_index` is the numeric column id; never the header text. */
export function trackTableSorted(props: {
    chat_id: string | null;
    column_index: number;
    direction: "asc" | "desc";
}): void {
    posthog.capture("User Sorted Table", props);
}

/** Fires only on the empty/non-empty transition; never per
 * keystroke. The filter text is never captured (PII). */
export function trackTableFiltered(props: {
    chat_id: string | null;
    has_filter: boolean;
}): void {
    posthog.capture("User Filtered Table", props);
}

export function trackPollyThreadExported(props: {
    conversation_id: number | null;
    format: "markdown" | "xlsx";
    message_count: number;
    /** Only set for the xlsx "All Tables" export: sheets in the workbook. */
    table_count?: number;
}): void {
    posthog.capture("User Exported Polly Thread", props);
}

export function trackScopeReportExported(props: {
    scope_id: number;
    export_type: BaseExportType;
    clause_count: number;
    include_guidance: boolean;
    include_comments: boolean;
    include_metadata: boolean;
}): void {
    posthog.capture("User Exported Scope Report", props);
}

export function trackComparisonExported(props: {
    comparison_id: number;
    export_type: BaseExportType;
    scope_count: number;
    clause_count: number;
    include_guidance: boolean;
    include_comments: boolean;
    include_metadata: boolean;
    include_fields: boolean;
}): void {
    posthog.capture("User Exported Comparison", props);
}
